import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { apiClient, formatApiError } from "@/lib/api";
import { KeyRound, Loader2, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

/** Force password change on first login (must_change_password=true). Also usable freely. */
export default function ChangePassword() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const forced = !!user?.must_change_password;
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const rules = [
    { ok: next.length >= 8, label: t("cp.rule8") },
    { ok: /[A-Z]/.test(next), label: t("cp.ruleUpper") },
    { ok: /\d/.test(next), label: t("cp.ruleDigit") },
    { ok: next && next === confirm, label: t("cp.ruleConfirm") },
  ];
  const allOk = rules.every((r) => r.ok);

  const submit = async (e) => {
    e.preventDefault();
    if (!allOk) return toast.error(t("cp.errRules"));
    if (!forced && !current) return toast.error(t("cp.errCurrent"));
    setBusy(true);
    try {
      await apiClient.post("/auth/change-password", {
        new_password: next,
        current_password: forced ? undefined : current,
      });
      toast.success(t("cp.success"));
      await refresh?.();
      // Route by role
      const role = user?.role;
      if (role === "restaurant") nav("/dashboard/restaurant", { replace: true });
      else if (role === "driver") nav("/driver", { replace: true });
      else if (role === "admin") nav("/dashboard/admin", { replace: true });
      else nav("/", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16" data-testid="change-password-page">
      <div className="bg-white rounded-2xl border border-sand p-6">
        <div className="w-12 h-12 rounded-2xl bg-brand-red/10 text-brand-red grid place-items-center mb-4">
          {forced ? <ShieldCheck className="w-6 h-6" /> : <KeyRound className="w-6 h-6" />}
        </div>
        <h1 className="font-display text-2xl font-semibold">
          {forced ? t("cp.titleForced") : t("cp.title")}
        </h1>
        <p className="text-sm text-mute mt-1">
          {forced ? t("cp.subtitleForced") : t("cp.subtitle")}
        </p>

        <form onSubmit={submit} className="mt-5 space-y-4">
          {!forced && (
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-mute block mb-1">{t("cp.current")}</span>
              <input
                type={show ? "text" : "password"}
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                autoComplete="current-password"
                required
                data-testid="cp-current"
                className="w-full bg-white border border-sand rounded-xl px-3 py-2.5 text-sm outline-none focus:border-brand-red"
              />
            </label>
          )}

          <label className="block">
            <span className="text-xs uppercase tracking-widest text-mute block mb-1">{t("cp.new")}</span>
            <div className="relative">
              <input
                type={show ? "text" : "password"}
                value={next}
                onChange={(e) => setNext(e.target.value)}
                autoComplete="new-password"
                required
                data-testid="cp-new"
                className="w-full bg-white border border-sand rounded-xl px-3 py-2.5 pr-10 text-sm outline-none focus:border-brand-red"
              />
              <button type="button" onClick={() => setShow((s) => !s)} data-testid="cp-toggle-show" className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg hover:bg-elevated" aria-label="Afficher/masquer">
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </label>

          <label className="block">
            <span className="text-xs uppercase tracking-widest text-mute block mb-1">{t("cp.confirm")}</span>
            <input
              type={show ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
              data-testid="cp-confirm"
              className="w-full bg-white border border-sand rounded-xl px-3 py-2.5 text-sm outline-none focus:border-brand-red"
            />
          </label>

          <ul className="space-y-1" data-testid="cp-rules">
            {rules.map((r) => (
              <li key={r.label} className={`text-xs flex items-center gap-1.5 ${r.ok ? "text-brand-green" : "text-mute"}`}>
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${r.ok ? "bg-brand-green" : "bg-mute/50"}`} />
                {r.label}
              </li>
            ))}
          </ul>

          <button
            type="submit"
            disabled={busy || !allOk || (!forced && !current)}
            data-testid="cp-submit"
            className="w-full rounded-full bg-brand-red hover:bg-[#C81F2D] disabled:opacity-60 text-white py-3 text-sm font-semibold inline-flex items-center justify-center gap-2"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {forced ? t("cp.activate") : t("cp.save")}
          </button>
        </form>
      </div>
    </div>
  );
}
